import { EMPTY, filter, firstValueFrom, merge, timeout, type Observable } from 'rxjs';
import { v4 as uuid } from 'uuid';

import { isDate } from 'lodash-es';
import { isDateParameterValue } from '../../../components/Forms';
import { DateField } from '../../../fields';
import { NEW_ORDER_SINGLE, ORDER_CANCEL_REQUEST, ORDER_CONTROL_REQUEST, ORDER_MODIFY_REQUEST } from '../../../tokens';
import { CustomerOrderStrategiesEnum } from '../../../tokens/customerOrderStrategies';
import type { IStrategyParameters, OrderStrategy, Parameter } from '../../../types';
import {
  ErrorActionEnum,
  ExecTypeEnum,
  OrdStatusEnum,
  OrdTypeEnum,
  OrderControlActionEnum,
  TimeInForceEnum,
  type CustomerOrder,
  type WhitelabelParametersFormState,
} from '../../../types';
import type { WLOrgConfig } from '../../../types/WLOrgConfig';
import { formattedDateForSubscription } from '../../../utils';
import { includesEnumValue } from '../../../utils/enumlikeHandlers';
import type { IWebSocketClient } from '../../WebSocketClient';
import { observeWLOrderByID } from '../observeWLOrderByID';
import { EXEC_REPORT_RESPONSE_TIMEOUT_MS } from '../tokens';
import type { WLAppStore } from '../WLOrderFormStore';
import {
  cleanState,
  selectError,
  selectOrderStep,
  setError,
  setOrderStep,
  type OrderFormState,
  type OrderState,
} from './../state';
import type { WhiteLabelNewOrderSingle, WhiteLabelOrderCancelReplace } from './types';

/**
 * Handles websocket communication around Whitelabel-Mobile Orders
 * - Places, modifies and cancels orders. Pauses and resumes as well.
 * - Does transformation of UI form model to order placement calls
 * - Triggers state changes and showing errors when hearing acks on orders
 */
export class OrderService {
  constructor(
    private store: WLAppStore,
    private webSocketClient: IWebSocketClient<CustomerOrder>,
    private config: { dismissesActionAfterTimeout: boolean } = { dismissesActionAfterTimeout: true },
    // Observable that emits singular order updates. Narrowed down to orders that update during the user session.
    private sessionOrdersObs: Observable<CustomerOrder>,
    private orgConfig?: WLOrgConfig
  ) {}

  public async cancelOrder(orderID: string, requireSuccessfullyCanceled: boolean = false): Promise<CustomerOrder> {
    const clOrdID = uuid();
    const cancelOrder = () => {
      this.webSocketClient.registerPublication({
        type: ORDER_CANCEL_REQUEST,
        data: [
          {
            ClOrdID: clOrdID,
            OrderID: orderID,
            TransactTime: formattedDateForSubscription(new Date()),
          },
        ],
      });
    };

    this.store.dispatch(setOrderStep({ step: 'cancel_pending', orderID }));
    const expectedOrdStatus = requireSuccessfullyCanceled ? [OrdStatusEnum.Canceled] : undefined;
    return this.createActionPromise({
      clOrderID: clOrdID,
      action: cancelOrder,
      orderID,
      expectedOrdStatus,
    })
      .then(order => {
        this.store.dispatch(setOrderStep({ step: 'cancel_confirmation', orderID: order.OrderID }));
        return order;
      })
      .catch(e => {
        this.store.dispatch(setOrderStep({ step: 'cancel_failed', orderID }));
        throw e;
      })
      .finally(() => {
        if (this.config.dismissesActionAfterTimeout) {
          setTimeout(() => {
            this.store.dispatch(setOrderStep({ step: 'idle' }));
          }, EXEC_REPORT_RESPONSE_TIMEOUT_MS);
        }
      });
  }

  public pauseOrder(orderID: string) {
    const clOrdID = uuid();
    this.webSocketClient.registerPublication({
      type: ORDER_CONTROL_REQUEST,
      data: [
        {
          ClOrdID: clOrdID,
          OrderID: orderID,
          Action: OrderControlActionEnum.Pause,
          TransactTime: formattedDateForSubscription(new Date()),
        },
      ],
    });
    return clOrdID;
  }

  public resumeOrder(orderID: string) {
    const clOrdID = uuid();
    this.webSocketClient.registerPublication({
      type: ORDER_CONTROL_REQUEST,
      data: [
        {
          ClOrdID: clOrdID,
          OrderID: orderID,
          Action: OrderControlActionEnum.Resume,
          TransactTime: formattedDateForSubscription(new Date()),
        },
      ],
    });
    return clOrdID;
  }

  private createActionPromise = ({
    action,
    clOrderID,
    orderID,
    expectedOrdStatus,
  }: {
    action: () => void;
    clOrderID: string;
    orderID?: string;
    expectedOrdStatus?: OrdStatusEnum[];
  }): Promise<CustomerOrder> => {
    // Observe the target before publishing so orders outside the session window are confirmed too.
    const orderByID = orderID ? observeWLOrderByID(this.webSocketClient, orderID) : EMPTY;
    const promise = firstValueFrom(
      merge(this.sessionOrdersObs, orderByID).pipe(
        filter(
          order =>
            (!orderID || order.OrderID === orderID) &&
            order.ClOrdID === clOrderID &&
            (expectedOrdStatus ? includesEnumValue(expectedOrdStatus, order.OrdStatus) : true)
        ),
        // If there is no response within the timeout, we reject the promise
        timeout(EXEC_REPORT_RESPONSE_TIMEOUT_MS)
      )
    );

    action();
    return promise;
  };

  public async requestOrder(): Promise<CustomerOrder | undefined> {
    const request = this.generateRequest();
    if (!request) {
      throw new Error('Unable to generate order');
    }
    this.debug('NewOrderSingle', request);
    this.store.dispatch(setOrderStep({ step: 'loading' }));

    const placeOrder = () => {
      this.webSocketClient.registerPublication({
        type: NEW_ORDER_SINGLE,
        data: [request],
      });
    };
    return this.createActionPromise({ clOrderID: request.ClOrdID, action: placeOrder })
      .then(order => {
        if (order.OrdStatus === OrdStatusEnum.Rejected) {
          this.store.dispatch(setOrderStep({ step: 'new_rejected', orderID: order.OrderID }));
          return;
        }
        this.store.dispatch(setOrderStep({ step: 'new_confirmation', orderID: order.OrderID }));
        return order;
      })
      .catch(() => {
        this.store.dispatch(setError({ text: 'no_order_response', clOrderID: request.ClOrdID }));
        return undefined;
      })
      .finally(() => {
        if (this.config.dismissesActionAfterTimeout) {
          const handleCleanup = () => {
            // If the orderstep is no longer newRejected or newConfirmation, it is likely that
            // the user have primed the order-form anew (or changed something within the order form),
            // and we should not clean the state if that is the case.
            const orderStep = selectOrderStep(this.store.getState());
            const error = selectError(this.store.getState());
            this.store.dispatch(setOrderStep({ step: 'idle' }));
            if (orderStep === 'new_rejected' || orderStep === 'new_confirmation' || error) {
              this.store.dispatch(cleanState({ keepSymbol: true, keepStrategy: false }));
            }
          };
          setTimeout(handleCleanup, EXEC_REPORT_RESPONSE_TIMEOUT_MS);
        }
      });
  }

  public async updateOrder() {
    const request = this.generateRequest(this.state.orderBeingModified);
    if (!request || !request.OrderID) {
      throw new Error('Unable to modify order');
    }
    this.debug('OrderCancelReplace', request);
    this.store.dispatch(setOrderStep({ step: 'loading', orderID: request.OrderID }));

    const updateOrder = () => {
      this.webSocketClient.registerPublication({
        type: ORDER_MODIFY_REQUEST,
        data: [request],
      });
    };
    return this.createActionPromise({ clOrderID: request.ClOrdID, action: updateOrder, orderID: request.OrderID })
      .then(order => {
        if (order.ExecType === ExecTypeEnum.ReplaceRejected || order.ExecType === ExecTypeEnum.UpdateRejected) {
          this.store.dispatch(setOrderStep({ step: 'update_rejected', orderID: order.OrderID }));
          return;
        }
        this.store.dispatch(setOrderStep({ step: 'update_confirmation', orderID: order.OrderID }));
      })
      .catch(() => {
        this.store.dispatch(setError({ text: 'no_order_response', clOrderID: request.ClOrdID }));
      })
      .finally(() => {
        if (this.config.dismissesActionAfterTimeout) {
          const handleCleanup = () => {
            // If the orderstep is no longer updateRejected or updateConfirmation, it is likely that
            // the user have primed the order-form anew (or changed something within the order form),
            // and we should not clean the state if that is the case.
            const orderStep = selectOrderStep(this.store.getState());
            const error = selectError(this.store.getState());
            this.store.dispatch(setOrderStep({ step: 'idle' }));
            if (orderStep === 'update_rejected' || orderStep === 'update_confirmation' || error) {
              this.store.dispatch(cleanState({ keepSymbol: true }));
            }
          };
          setTimeout(handleCleanup, EXEC_REPORT_RESPONSE_TIMEOUT_MS);
        }
      });
  }

  private generateRequest(order: CustomerOrder | undefined): WhiteLabelOrderCancelReplace | undefined;
  private generateRequest(): WhiteLabelNewOrderSingle | undefined;
  private generateRequest(order?: CustomerOrder): WhiteLabelNewOrderSingle | WhiteLabelOrderCancelReplace | undefined {
    const { symbolField, sideField, orderCurrencyField, quantityField, strategyField, marketAccountField } =
      this.formState;

    const isModifyingOrderWithoutStrategy = order && order.Strategy === undefined;

    // IFF modifying an order, we allow strategy to be empty
    if (strategyField.value === undefined && !isModifyingOrderWithoutStrategy) {
      return;
    }

    const transactTime = new Date();
    const parameters = { ...order?.Parameters, ...this.getParameters() };

    const modifiedParameters = cleanParametersForOrder(parameters, strategyField.value);

    const ordType: OrdTypeEnum = strategyField.value?.OrdType ?? order?.OrdType ?? OrdTypeEnum.Limit;
    let timeInForce: TimeInForceEnum = TimeInForceEnum.GoodTillCancel;

    if (ordType === OrdTypeEnum.Market) {
      delete modifiedParameters.LimitPrice;

      // [DEAL-5394] Check if it's a Market strategy - these should use the configured default TimeInForce, fallback to FillOrKill
      if (strategyField.value?.Name === CustomerOrderStrategiesEnum.Market || strategyField.value?.Name === '') {
        timeInForce = this.orgConfig?.defaultMarketOrderTimeInForce ?? TimeInForceEnum.FillOrKill;
      } else {
        // [DEAL-5394] For other OrdType Market strategies, continue to use FillOrKill
        timeInForce = TimeInForceEnum.FillOrKill;
      }
    }

    if (!symbolField.value || !sideField.value || !quantityField.value || !orderCurrencyField.value) {
      return;
    }

    const clOrdId = uuid();

    // modifying
    if (order) {
      const modifyRequest: WhiteLabelOrderCancelReplace = {
        Symbol: symbolField.value.Symbol,
        ClOrdID: clOrdId,
        Side: sideField.value,
        OrderQty: quantityField.value,
        Price: modifiedParameters.LimitPrice,
        Currency: orderCurrencyField.value,
        Strategy: order.Strategy,
        MarketAccount: marketAccountField.value?.SourceAccountID,
        TransactTime: formattedDateForSubscription(transactTime),
        TimeInForce: timeInForce,
        OrdType: ordType,
        Parameters: modifiedParameters,
        OrderID: order.OrderID,
      };
      return modifyRequest;
    }
    if (!strategyField.value) {
      return;
    }

    // placing new
    const request: WhiteLabelNewOrderSingle = {
      Symbol: symbolField.value.Symbol,
      ClOrdID: clOrdId,
      Side: sideField.value,
      OrderQty: quantityField.value,
      Price: modifiedParameters.LimitPrice,
      Currency: orderCurrencyField.value,
      Strategy: strategyField.value?.Name,
      MarketAccount: marketAccountField.value?.SourceAccountID,
      TransactTime: formattedDateForSubscription(transactTime),
      TimeInForce: timeInForce,
      OrdType: ordType,
      Parameters: modifiedParameters,
    };

    return request;
  }

  private getParameters(): Record<keyof IStrategyParameters, any> {
    const params = Object.keys(this.formState.parameters).reduce((acc: Record<string, any>, key) => {
      const typedKey = key as keyof IStrategyParameters;
      // We need to send explicit null for Dates if user cleared a previously set value; if we don't send it or send undefined the previous value will be kept
      const shouldIgnoreDate =
        this.formState.parameters[typedKey] instanceof DateField &&
        !this.state.orderBeingModified?.Parameters?.[typedKey];

      const shouldIgnore = !this.formState.parameters[typedKey].hasValue && shouldIgnoreDate;
      if (shouldIgnore) {
        return acc;
      }
      acc[typedKey] = this.formState.parameters[typedKey].value ?? null;
      return acc;
    }, {});
    // Force this parameter on all orders
    params['ErrorAction'] = ErrorActionEnum.Pause;
    return params;
  }

  private debug(key: string, value: any) {
    // dispatch an action so it's logged and can be debugged in the redux debugger but isn't actually handled by reducer and does not affect state in any ways
    this.store.dispatch({ type: `order/debug/${key}`, payload: value });
  }

  private get formState(): OrderFormState {
    return this.state.form;
  }

  private get state(): OrderState {
    return this.store.getState().order;
  }
}

function cleanParametersForOrder(
  currParameters: WhitelabelParametersFormState,
  selectedStrategy: OrderStrategy | undefined
): WhitelabelParametersFormState {
  // Don't modify original parameters obj
  const parameters = { ...currParameters };

  // If no strategy, do not clean parameters
  if (selectedStrategy) {
    // Delete Incorrect and Unused Strategy Parameters
    removeUnusedParameters(parameters, selectedStrategy.Parameters);
  }
  // format the date parameters
  formatDateParameters(parameters, selectedStrategy?.Parameters);

  // This will later be removed and set from the WLOrderStrategy subscription
  return { ...parameters, ErrorAction: ErrorActionEnum.Pause };
}

function removeUnusedParameters(parameters: WhitelabelParametersFormState, selectedParameters: Parameter[]) {
  for (const key in parameters) {
    if (parameters[key] == null || parameters[key] === '' || !selectedParameters?.map(p => p.Name)?.includes(key)) {
      delete parameters[key];
    }
  }
  return parameters;
}

function formatDateParameters(parameters: WhitelabelParametersFormState, selectedParameters: Parameter[] | undefined) {
  for (const key in parameters) {
    const param = selectedParameters?.find(p => p.Name === key);

    const dateValue = parameters[key];
    if (
      isDateParameterValue(dateValue, param?.Type) ||
      (parameters[key] instanceof DateField && (typeof dateValue === 'string' || isDate(dateValue)))
    ) {
      parameters[key] = param != null ? formattedDateForSubscription(dateValue) : parameters[key];
    }
  }
}
